import { PhotoModel } from './protocols';
import { Attachment } from './attachments';
import { AssigneeMeta, DelegationMeta } from './assignees';

export enum ProjectStatus {
    PLANNED = "Planerad",
    IN_PROGRESS = "Pågående",
    COMPLETED = "Utförd"
}

export interface ProjectNote {
    key: string;
    title: string;
    content: string;
    createdBy: string; // UID
    createdAt: string; // YYYY-MM-DD
    updatedBy?: string; // UID
    updatedAt?: string; // YYYY-MM-DD
}

export interface ChecklistItem {
    key: string;
    text: string;
    completed: boolean;
    createdBy?: string; // UID; stamped by web and the guest API, which gates item deletion on it
    completedBy?: string; // UID
    completedAt?: string; // YYYY-MM-DD
    sortOrder?: number; // For drag-and-drop ordering
}

export interface ProjectChecklist {
    key: string;
    title: string;
    items: { [key: string]: ChecklistItem };
    createdBy: string; // UID
    createdAt: string; // YYYY-MM-DD
}

/**
 * A contact linked to a project — internal team member or external party.
 * Stored under projects/{key}/externalContacts/{userId} (the node name is kept
 * for backward compatibility; the list is surfaced in the UI as "Kontaktlista").
 */
export interface ProjectExternalContact {
    externalUserId: string; // UID of the linked user (also the node key)
    /**
     * Whether the linked user is an internal team member or an external party.
     * Absent on legacy records, which are all external — treat undefined as 'external'.
     */
    contactType?: 'internal' | 'external';
    addedBy: string; // UID of who added
    addedAt: string; // YYYY-MM-DD hh:mm:ss
    projectNote?: string; // Project-specific context
}

/**
 * Allocation of a project's budget across the three categories (kr).
 * The project's total budget is the sum of these three amounts — no scalar
 * total is stored; compute it with sumAllocation() from Helpers/budgetAllocation.
 */
export interface BudgetAllocation {
    investmentAmount: number; // Investering
    buildingMaintenanceAmount: number; // Underhåll byggnad
    propertyTechMaintenanceAmount: number; // Underhåll fastighetsteknik
}

export interface ProjectModel {
    key: string;
    buildingKey: string;
    title: string;
    description?: string; // Free-text project description
    status: ProjectStatus;
    startDate: string; // YYYY-MM-DD
    plannedEndDate: string; // YYYY-MM-DD
    actualEndDate?: string; // YYYY-MM-DD (set when status = COMPLETED)
    budget?: BudgetAllocation; // Budget split across the three categories (total = sum)
    beforePictures?: { [key: string]: PhotoModel };
    progressPictures?: { [key: string]: PhotoModel };
    afterPictures?: { [key: string]: PhotoModel };
    notes?: { [key: string]: ProjectNote };
    checklists?: { [key: string]: ProjectChecklist };
    attachments?: { [key: string]: Attachment };

    // Assignment — multi-assignee model, mirrors WorkOrder. Read via
    // getAssignees()/getDelegations(); writers emit only the maps and delete the
    // legacy scalars below.
    assignees?: { [internalUid: string]: AssigneeMeta };          // internal users
    delegations?: { [externalUserId: string]: DelegationMeta };   // external delegates (the grant; each reaches it via their personal link)

    // Beställare — purely informational references to internal users.
    // No behaviour attached; surfaced in web admin and read-only on iOS.
    // NOT part of the multi-assignee model (single informational UIDs).
    responsibleOrderer?: string;  // Ansvarig beställare (UID, undefined = ej angiven)
    secondaryOrderer?: string;    // Sekundär beställare (UID, undefined = ej angiven)

    // Legacy single-assignee scalars — READ-ONLY via the normalizers, never
    // written going forward. Kept for the read-shim until the one-time backfill.
    /** @deprecated read via getAssignees(); folded into `assignees` */
    assignedTo?: string;  // UID for internal user
    /** @deprecated read via getDelegations(); folded into `delegations` */
    externalUserId?: string;   // References users/userList/{uid}
    /** @deprecated read via getDelegations(); folded into `delegations` */
    guestTokenKey?: string;    // Reference to guest access token

    // Transient opt-in flag written alongside newly-added assignees to request
    // the assignment email. Consumed and cleared by projectAssignmentNotification.
    sendAssignmentEmail?: boolean;

    externalContacts?: { [externalUserId: string]: ProjectExternalContact };
    createdBy: string; // UID
    createdAt: string; // YYYY-MM-DD
    updatedBy?: string; // UID
    updatedAt?: string; // YYYY-MM-DD
}

